import React, { useEffect, useState } from 'react';
import FormModal from '../../Modals/FormModal';
import InputField from '../../Forms/Input/InputField';
import SelectField from '../../Forms/Input/SelectField';
import ToggleSwitch from '../../Forms/Input/ToggleSwitch';
import clsx from 'clsx';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useQzTrayConfiguration } from '../../../hooks/qztray/useQzTrayConfiguration';
import { QzTrayConfigurationForm } from '../../../types/qztray/QzTrayConfigurationTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: QzTrayConfigurationForm) => void;
  isSubmitting: boolean;
}

const QzTrayConfigurationEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const { branchDropdown, fetchBranchDropdown } = useBranch();
  const { activeConfiguration, testConnection } = useQzTrayConfiguration(); // ✅ usamos testConnection del hook
  const [form, setForm] = useState<QzTrayConfigurationForm>({
    host: '',
    port: 8181,
    protocol: 'ws',
    sslEnabled: false,
    timeoutMs: 5000,
    tenantId: '',
    branchId: null,
  });

  useEffect(() => {
    if (isOpen) {
      fetchBranchDropdown();

      if (activeConfiguration) {
        setForm({
          host: activeConfiguration.host,
          port: activeConfiguration.port,
          protocol: activeConfiguration.protocol,
          sslEnabled: activeConfiguration.sslEnabled,
          timeoutMs: activeConfiguration.timeoutMs,
          tenantId: activeConfiguration.tenantId,
          branchId: activeConfiguration.branchId,
        });
      }
    }
  }, [isOpen, activeConfiguration]);

  const updateField = (field: keyof QzTrayConfigurationForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Configuración QZ Tray"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="mt-4 grid grid-cols-1 gap-5">
        {/* 🏬 Sucursal */}
        <SelectField
          label="Sucursal"
          name="branchId"
          value={form.branchId ?? ''}
          onChange={(e) => updateField('branchId', Number(e.target.value))}
          options={branchDropdown.map((b: any) => ({
            label: b.name,
            value: b.id,
          }))}
          placeholder="Seleccione una sucursal"
          iconKey="building"
          required
        />

        {/* 🌐 Host */}
        <InputField
          label="Host"
          name="host"
          type="text"
          value={form.host}
          onChange={(e) => updateField('host', e.target.value)}
          placeholder="Ejemplo: localhost o 192.168.1.10"
          iconKey="network"
          required
        />

        {/* 🔌 Puerto */}
        <InputField
          label="Puerto"
          name="port"
          type="number"
          value={form.port}
          onChange={(e) => updateField('port', Number(e.target.value))}
          placeholder="Ejemplo: 8181"
          iconKey="server"
          required
        />

        {/* 🛡️ Protocolo */}
        <SelectField
          label="Protocolo"
          name="protocol"
          value={form.protocol}
          onChange={(e) =>
            updateField('protocol', e.target.value as 'ws' | 'wss')
          }
          options={[
            { label: 'ws (no seguro)', value: 'ws' },
            { label: 'wss (seguro con SSL)', value: 'wss' },
          ]}
          placeholder="Seleccione protocolo"
          iconKey="shield"
          required
        />

        {/* ⏱️ Timeout + Tenant ID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <InputField
            label="Tiempo de espera (ms)"
            name="timeoutMs"
            type="number"
            value={form.timeoutMs}
            onChange={(e) => updateField('timeoutMs', Number(e.target.value))}
            placeholder="Ejemplo: 5000"
            iconKey="clock"
            required
          />

          <InputField
            label="Tenant ID"
            name="tenantId"
            type="text"
            value={form.tenantId}
            onChange={(e) => updateField('tenantId', e.target.value)}
            placeholder="Identificador del cliente (tenant)"
            iconKey="idCard"
            required
          />
        </div>

        {/* 🔒 SSL habilitado */}
        <div className="flex items-center justify-between rounded-lg border border-stroke px-4 py-2 dark:border-strokedark">
          <ToggleSwitch
            label="SSL habilitado"
            name="sslEnabled"
            checked={form.sslEnabled}
            onChange={() => updateField('sslEnabled', !form.sslEnabled)}
            activeColor="primary"
            inactiveColor="gray"
          />
        </div>

        {/* 💾 / 🧪 Botones de acción */}
        <div className="flex flex-col sm:flex-row gap-3 pt-3">
          {/* 🧪 Botón Probar conexión */}
          <button
            type="button"
            onClick={() => testConnection(form)}
            disabled={isSubmitting}
            className={clsx(
              'w-full sm:w-1/2 rounded px-4.5 py-2.5 font-medium text-white',
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#17A2B8] hover:bg-[#138496]'
            )}
          >
            {isSubmitting ? 'Probando conexión...' : 'Probar conexión'}
          </button>

          {/* 💾 Botón Guardar */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={clsx(
              'w-full sm:w-1/2 rounded px-4.5 py-2.5 font-medium text-white',
              isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary hover:bg-opacity-90'
            )}
          >
            {isSubmitting
              ? 'Actualizando configuración...'
              : 'Actualizar Configuración'}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default QzTrayConfigurationEditModal;
